import { Component, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { User } from '@angular/fire/auth';
import { filter, take } from 'rxjs';
import { AuthService } from '@app/core/services/auth.service';
import { PartnerApplication, PartnerApplicationStatus } from '../../models/partner.models';
import { PartnerApplicationService } from '../../services/partner-application';
import { PartnerRoleConfigService } from '../../services/partner-role-config';


const SEEN_KEY_PREFIX = 'az-partner-app-seen:';

/**
 * A banner surfacing the applicant's current application state to a
 * logged-in user. Two different behaviors depending on status:
 *
 *  - 'pending'            → shown on EVERY visit, no permanent dismiss.
 *                            There's no one-time "decision" here — it's an
 *                            ongoing state — so a session-only dismiss
 *                            (hides for this page view, reappears next
 *                            visit) is correct; a permanent dismiss would
 *                            mean the applicant never gets reminded there's
 *                            still no decision.
 *  - 'approved'/'rejected' → shown ONCE. Dismissing marks that specific
 *                            application id as seen in localStorage, so it
 *                            won't reappear on this device once
 *                            acknowledged. This is a real, one-time
 *                            decision — nagging about it forever would be
 *                            noise, not signal.
 *
 * "Seen" state is per-device (localStorage), not server-tracked — a real
 * limitation, acceptable for a v1 notification mechanism that otherwise
 * has no push/email behind it at all.
 *
 * Currently wired into partner-landing only. To surface this in the main
 * product (outside /execution-partners), drop <app-partner-status-banner>
 * into the global header/shell component once available.
 */
@Component({
  selector: 'app-partner-status-banner',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule],
  templateUrl: './partner-status-banner.html',
  styleUrls: ['./partner-status-banner.css'],
})
export class PartnerStatusBanner implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly applications = inject(PartnerApplicationService);
  private readonly roleConfig = inject(PartnerRoleConfigService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  visible = false;
  application: PartnerApplication | null = null;
  roleLabel = '';

  ngOnInit(): void {
    console.log('[PartnerStatusBanner] ngOnInit fired. isBrowser =', this.isBrowser);
    if (!this.isBrowser) {
      console.log('[PartnerStatusBanner] not browser — SSR pass, skipping.');
      return;
    }

    this.auth.user$
      .pipe(
        filter((u): u is User | null => u !== undefined),
        take(1),
      )
      .subscribe(async (user) => {
        console.log('[PartnerStatusBanner] auth resolved. uid =', user?.uid ?? null);
        if (!user) {
          console.log('[PartnerStatusBanner] no user — not logged in, stopping.');
          return;
        }
        try {
          console.log('[PartnerStatusBanner] calling getMyLatestApplication…');
          const latest = await this.applications.getMyLatestApplication(user.uid);
          console.log('[PartnerStatusBanner] getMyLatestApplication resolved:', latest);

          if (!latest) {
            console.log('[PartnerStatusBanner] no application found for this uid — stopping.');
            return;
          }

          const status: PartnerApplicationStatus = latest.status;
          console.log('[PartnerStatusBanner] application status =', status);

          if (status === 'pending') {
            console.log('[PartnerStatusBanner] pending → showing banner.');
            this.application = latest;
            this.roleLabel = this.roleConfig.getRoleConfig(latest.role)?.label ?? latest.role;
            this.visible = true;
            return;
          }

          if (status === 'approved' || status === 'rejected') {
            const seen = this.hasBeenSeen(latest.id);
            console.log('[PartnerStatusBanner] approved/rejected. already seen? =', seen);
            if (seen) return;
            this.application = latest;
            this.roleLabel = this.roleConfig.getRoleConfig(latest.role)?.label ?? latest.role;
            this.visible = true;
          }
        } catch (err) {
          // Non-fatal for the user — no banner is a reasonable fallback —
          // but log it so an actual problem (e.g. a missing Firestore
          // composite index on uid + createdAt) doesn't fail invisibly.
          console.error('[PartnerStatusBanner] error while checking application status:', err);
        }
      });
  }

  /** Session-only for 'pending' (nothing persisted — it'll be back next
   *  visit); permanent for 'approved'/'rejected' (marks seen so it won't
   *  return on this device). */
  dismiss(): void {
    if (this.application && this.application.status !== 'pending') {
      this.markSeen(this.application.id);
    }
    this.visible = false;
  }

  private hasBeenSeen(applicationId: string): boolean {
    try {
      return localStorage.getItem(SEEN_KEY_PREFIX + applicationId) === '1';
    } catch {
      return false;
    }
  }

  private markSeen(applicationId: string): void {
    try {
      localStorage.setItem(SEEN_KEY_PREFIX + applicationId, '1');
    } catch {
      // Storage unavailable (private browsing, quota, etc.) — the banner
      // will just reappear next visit, which is a harmless fallback.
    }
  }
}